import { HOMEPAGE_FAQS } from '@/lib/seo/homepage-faqs';
import { aggregateRatingNode } from '@/lib/seo/social-proof';
import {
  ORG_ID,
  SITE_ID,
  SOFTWARE_ID,
  FOUNDER_ID,
  LOCAL_BIZ_ID,
  GBP_MAPS_URL,
  SITE_BASE as BASE,
  ORG_SAME_AS,
  abs,
} from '@/lib/seo/entities';
import { wrapGraph } from '@/lib/seo/schema-builders';
import { BIZRNR_NAP, NAP_POSTAL_ADDRESS } from '@/lib/seo/nap';

/**
 * Root-layout JSON-LD emitters. Rendered on EVERY page (see app/layout.tsx).
 *
 * Stable @id values (ORG_ID / SITE_ID / SOFTWARE_ID — imported from
 * src/lib/seo/entities.ts) match the homepage's HomepageGraphJsonLd so
 * Google merges duplicates on the homepage and the sitewide knowledge
 * graph stays one entity.
 */

// Re-export for backwards compatibility — existing callsites import these
// from this file. New code should import from '@/lib/seo/entities'.
export { ORG_ID, SITE_ID, SOFTWARE_ID, LOCAL_BIZ_ID };

export function OrganizationJsonLd() {
  const data = {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: 'BizRnR',
    alternateName: ['Business Runner', 'BizRnR AI'],
    url: BASE,
    logo: {
      '@type': 'ImageObject',
      url: `${BASE}/opengraph-image`,
      width: 1200,
      height: 630,
    },
    description: 'AI voice agent for small business — answers every call, qualifies leads, and books appointments 24/7.',
    foundingDate: '2024',
    foundingLocation: { '@type': 'Place', name: 'San Diego, California, USA' },
    address: NAP_POSTAL_ADDRESS,
    founder: {
      '@type': 'Person',
      '@id': FOUNDER_ID,
      name: 'Kristian Peter',
      jobTitle: 'Founder & CEO',
    },
    areaServed: [
      { '@type': 'Country', name: 'United States' },
      { '@type': 'Country', name: 'Canada' },
    ],
    knowsLanguage: ['en', 'en-US'],
    knowsAbout: [
      'AI voice automation',
      'AI phone answering',
      'Lead qualification',
      'Appointment booking',
      'Customer relationship management',
      'Conversational AI',
      'Small business automation',
      'Speech synthesis',
      'Telephony',
      'Sales enablement',
      'AI receptionist for real estate',
      'Real estate lead response',
      'Property management call answering',
      'AI answering service for real estate brokers',
    ],
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'sales',
        url: `${BASE}/auth/register`,
        availableLanguage: 'English',
      },
      {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: 'support@bizrnr.com',
        availableLanguage: 'English',
      },
    ],
    sameAs: [...ORG_SAME_AS],
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(wrapGraph([data])) }} />;
}

export function WebSiteJsonLd() {
  const data = {
    '@type': 'WebSite',
    '@id': SITE_ID,
    name: 'BizRnR',
    alternateName: 'BizRnR — AI Voice Automation',
    url: BASE,
    inLanguage: 'en-US',
    publisher: { '@id': ORG_ID },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${BASE}/blog?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(wrapGraph([data])) }} />;
}

/**
 * Root SoftwareApplication — includes `offers` so every non-homepage page
 * ships a Google-valid SoftwareApplication. Homepage's HomepageGraphJsonLd
 * uses the same @id, so Google merges them.
 *
 * NOTE (2026-07-04): `aggregateRating` was RESTORED, sourced from the
 * canonical single-source-of-truth (src/lib/seo/social-proof.ts) so every
 * non-homepage page carries the SAME 4.8/127 rating as the homepage. KP
 * attested the reviews are real client reviews. This supersedes the earlier
 * removal.
 */
export function SoftwareApplicationJsonLd() {
  const data = {
    '@type': 'SoftwareApplication',
    '@id': SOFTWARE_ID,
    name: 'BizRnR AI Voice Automation',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description: 'AI-powered voice automation platform that calls, qualifies, and converts business leads 24/7 without human intervention.',
    url: BASE,
    screenshot: `${BASE}/opengraph-image`,
    publisher: { '@id': ORG_ID },
    provider: { '@id': ORG_ID },
    featureList: [
      'AI Voice Calls',
      'Lead Qualification',
      'Appointment Booking',
      'Built-in CRM',
      '24/7 Autonomous Automation',
      'Email Outreach Sequences',
      'SMS Follow-up',
      'Real-time Pipeline Dashboard',
    ],
    aggregateRating: aggregateRatingNode(),
    offers: {
      '@type': 'Offer',
      name: 'BizRnR subscription and enterprise automation',
      priceCurrency: 'USD',
      url: `${BASE}/pricing`,
      availability: 'https://schema.org/InStock',
      description: 'AI receptionist subscriptions and custom enterprise automation are available through BizRnR pricing.',
    },
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(wrapGraph([data])) }} />;
}

/**
 * Reads from the single source of truth at src/lib/seo/homepage-faqs.ts
 * so this schema stays 1:1 with what the FAQSection actually renders on
 * the page — Google suppresses FAQ rich results when the schema includes
 * questions that are not visibly present in the DOM.
 */
export function FAQJsonLd() {
  const data = {
    '@type': 'FAQPage',
    mainEntity: HOMEPAGE_FAQS.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(wrapGraph([data])) }} />;
}

// NOTE (2026-07-04): the sitewide AggregateRating now lives directly on the
// SoftwareApplication node above (sourced from src/lib/seo/social-proof.ts),
// and the homepage additionally emits the per-review Review array via
// HomepageGraphJsonLd. There is intentionally no standalone ReviewsJsonLd
// export — one canonical rating, consumed sitewide.

/**
 * HowTo schema for the homepage "How It Works" section.
 * Makes BizRnR eligible for HowTo rich results on Google.
 */
export function HowToJsonLd() {
  const data = {
    '@type': 'HowTo',
    name: 'How to deploy an AI voice agent with BizRnR',
    description: 'Set up a 24/7 AI voice agent that answers business calls, qualifies leads, and books appointments — in under 60 seconds.',
    totalTime: 'PT60S',
    estimatedCost: { '@type': 'MonetaryAmount', currency: 'USD', value: '0' },
    supply: [
      { '@type': 'HowToSupply', name: 'A business phone number (or BizRnR provisions one)' },
      { '@type': 'HowToSupply', name: 'A calendar (Google Calendar, Microsoft, or iCal)' },
    ],
    tool: [{ '@type': 'HowToTool', name: 'BizRnR account (30-day guarantee)' }],
    step: [
      {
        '@type': 'HowToStep',
        position: 1,
        name: 'Connect',
        text: 'Link your phone number, calendar, and CRM in minutes. No coding required.',
        url: `${BASE}/#how-it-works`,
      },
      {
        '@type': 'HowToStep',
        position: 2,
        name: 'Configure',
        text: "Customize your AI agent's personality, scripts, and qualification criteria.",
        url: `${BASE}/#how-it-works`,
      },
      {
        '@type': 'HowToStep',
        position: 3,
        name: 'Convert',
        text: 'Watch your AI agent handle calls, book appointments, and qualify leads 24/7.',
        url: `${BASE}/#how-it-works`,
      },
    ],
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(wrapGraph([data])) }} />;
}

/**
 * BreadcrumbList schema for inner pages.
 */
export function BreadcrumbJsonLd({ items }: { items: { name: string; url: string }[] }) {
  const data = {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({ '@type': 'ListItem', position: i + 1, name: item.name, item: item.url })),
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(wrapGraph([data])) }} />;
}

/**
 * LocalBusiness JSON-LD — aligned with the verified Google Business Profile.
 * Rendered in the root layout so every page carries the local signal.
 * `geo` and `priceRange` are intentionally omitted per plan.
 */
export function LocalBusinessJsonLd() {
  const schema = {
    '@type': 'LocalBusiness',
    '@id': LOCAL_BIZ_ID,
    'name': BIZRNR_NAP.name,
    'url': abs('/'),
    'description': 'Business Runner provides an AI voice receptionist, business phone, customer management, and automation tools for small businesses.',
    'telephone': BIZRNR_NAP.phoneDisplay,
    'address': NAP_POSTAL_ADDRESS,
    'openingHoursSpecification': [{
      '@type': 'OpeningHoursSpecification',
      'dayOfWeek': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      'opens': '00:00',
      'closes': '23:59',
    }],
    'hasMap': GBP_MAPS_URL,
    'image': abs('/opengraph-image'),
    'sameAs': ORG_SAME_AS,
    'parentOrganization': { '@id': ORG_ID },
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(wrapGraph([schema])) }}
    />
  );
}
